import { createDictationController } from './index.js';
import { createTranscriptState } from './transcript-state.js';
import { buildRecognitionContext } from './recognition-context.js';

const busyStates = new Set(['requesting_permission', 'connecting', 'listening', 'finalizing']);

/** Adapt the shared microphone transport to the existing historical-message editor. */
export default {
  inject: {
    dictationSubmissionBlocked: { default: () => () => false },
    dictationPublishTyping: { default: () => () => {} },
  },
  data() {
    return {
      dictationEnabled: window.lexiusChatDictationEnabled === true,
      dictationState: 'idle',
      dictationError: null,
      dictationRecovery: '',
      dictationCopied: false,
      dictationCallActive: window.lexiusCallMicrophoneActive === true,
      inlineSelection: { start: 0, end: 0 },
      inlineMaximumCharacters: 120000,
    };
  },
  created() {
    this._inlineGeneration = 0;
    this._inlineOwner = Symbol('inline-dictation');
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onInlineVisibility);
    window.addEventListener('pagehide', this.cancelInlineDictation);
    window.addEventListener('lexius:logout', this.onInlineLogout);
    window.addEventListener('lexius:microphone-call', this.onInlineCall);
    window.addEventListener('lexius:dictation-editor-open', this.onOtherInlineEditor);
    this._inlineModal = document.getElementById('chatModal');
    this._inlineModal?.addEventListener('hide.bs.modal', this.cancelInlineDictation);
  },
  beforeUnmount() {
    this.cancelInlineDictation();
    document.removeEventListener('visibilitychange', this.onInlineVisibility);
    window.removeEventListener('pagehide', this.cancelInlineDictation);
    window.removeEventListener('lexius:logout', this.onInlineLogout);
    window.removeEventListener('lexius:microphone-call', this.onInlineCall);
    window.removeEventListener('lexius:dictation-editor-open', this.onOtherInlineEditor);
    this._inlineModal?.removeEventListener('hide.bs.modal', this.cancelInlineDictation);
  },
  computed: {
    dictationBusy() { return busyStates.has(this.dictationState); },
    dictationStarting() {
      return this.dictationState === 'requesting_permission' || this.dictationState === 'connecting';
    },
    inlineDictationUnavailable() {
      return !this.editing || this.dictationCallActive || this.replying || this.dictationSubmissionBlocked();
    },
    inlineTextTooLong() { return String(this.editText || '').length > this.inlineMaximumCharacters; },
    inlineCanConfirm() {
      return this.editing && this.canEdit && !this.dictationBusy && !this.dictationSubmissionBlocked()
        && !this.inlineTextTooLong && Boolean(String(this.editText || '').trim());
    },
    dictationStatusText() {
      if (this.dictationError) return this.dictationError.message;
      if (this.dictationState === 'requesting_permission') return 'Permite el micrófono en el aviso del navegador. Puedes cancelar mientras esperas.';
      if (this.dictationState === 'connecting') return 'Conectando el dictado… Espera para hablar.';
      if (this.dictationState === 'listening') return 'Escuchando… ya puedes hablar.';
      if (this.dictationState === 'review') return 'Dictado detenido. Revisa el texto de la transcripción.';
      if (this.dictationState === 'finalizing') return 'Terminando el último fragmento…';
      if (this.dictationCallActive) return 'El dictado estará disponible cuando termine la llamada.';
      return '';
    },
  },
  watch: {
    editText: {
      flush: 'sync',
      handler() { if (!this._inlineWriting) this.pauseInlineDictationForEdit(); },
    },
    editing: {
      flush: 'sync',
      handler(value) { if (!value) this.cancelInlineDictation(); },
    },
    chatUuid: {
      flush: 'sync',
      handler() { this.cancelInlineDictation(); this.editing = false; },
    },
    message: {
      flush: 'sync',
      handler() { this.cancelInlineDictation(); this.editing = false; },
    },
    replying: {
      flush: 'sync',
      handler(value) { if (value) this.cancelInlineDictation(); },
    },
  },
  methods: {
    beginInlineDictationEditor() {
      window.dispatchEvent(new CustomEvent('lexius:dictation-editor-open', { detail: { owner: this._inlineOwner } }));
      this.cancelInlineDictation();
      this.dictationRecovery = '';
      this.dictationCopied = false;
      this.$nextTick(() => this.resizeInlineTextarea());
    },
    setInlineDictationState(state, error = null) {
      const wasBusy = this.dictationBusy;
      this.dictationState = state;
      this.dictationError = error;
      if (wasBusy !== this.dictationBusy) {
        window.dispatchEvent(new CustomEvent('lexius:dictation-editor-state', { detail: { active: this.dictationBusy } }));
      }
    },
    rememberInlineSelection(event) {
      const input = event?.target || this.$refs.editInput;
      if (input && typeof input.selectionEnd === 'number') {
        this.inlineSelection = { start: input.selectionStart, end: input.selectionEnd };
      }
    },
    resizeInlineTextarea() {
      const input = this.$refs.editInput;
      if (!input) return;
      input.style.height = 'auto';
      input.style.height = `${Math.min(input.scrollHeight, 200)}px`;
      input.style.overflowY = input.scrollHeight > 200 ? 'auto' : 'hidden';
      this.dictationPublishTyping(Boolean(String(this.editText || '').trim()));
    },
    onInlineInput(event) {
      this.pauseInlineDictationForEdit();
      this.rememberInlineSelection(event);
      this.resizeInlineTextarea();
    },
    pauseInlineDictationForEdit() {
      if (!this._inlineTranscript || !this.dictationBusy) return;
      this._inlineTranscript.freeze();
      this._inlineController?.freezeForEdit();
    },
    toggleInlineDictation() {
      if (this.dictationStarting) { this.cancelInlineDictation(); return; }
      if (this.dictationState === 'listening') { this._inlineController?.stop(); return; }
      if (this.dictationBusy || !this.dictationEnabled || this.inlineDictationUnavailable) return;
      this.rememberInlineSelection();
      this._inlineController?.dispose();
      const generation = ++this._inlineGeneration;
      const message = this.message;
      const chatUuid = this.chatUuid;
      const transcript = createTranscriptState(String(this.editText || ''), this.inlineSelection.end);
      this._inlineTranscript = transcript;
      const isCurrent = () => this.editing && generation === this._inlineGeneration
        && message === this.message && chatUuid === this.chatUuid;
      const receive = (kind, event) => {
        if (!isCurrent()) return;
        if (event.editable === false) transcript.freeze();
        const snapshot = transcript[kind](event.text);
        if (snapshot.frozen) {
          if (snapshot.recovery) { this.dictationRecovery = snapshot.recovery; this.dictationCopied = false; }
          return;
        }
        this._inlineWriting = true;
        this.editText = snapshot.text;
        this._inlineWriting = false;
        this.inlineSelection = { start: snapshot.caret, end: snapshot.caret };
        this.$nextTick(() => {
          if (!isCurrent() || transcript.snapshot().frozen) return;
          this.$refs.editInput?.setSelectionRange(snapshot.caret, snapshot.caret);
          this.resizeInlineTextarea();
        });
      };
      this._inlineController = createDictationController({
        onState: ({ state, error }) => { if (isCurrent()) this.setInlineDictationState(state, error || null); },
        onPartial: event => receive('partial', event),
        onCommitted: event => receive('commit', event),
        onMetric: metric => {
          if (!isCurrent()) return;
          window.dispatchEvent(new CustomEvent('lexius:dictation-metric', { detail: metric }));
          if (['connection_failure', 'finalization_timeout'].includes(metric.name)) {
            window.Sentry?.captureMessage?.(`chat_dictation.${metric.name}`, {
              level: 'warning', tags: { dictation_reason: metric.reason || 'final_commit' },
            });
          }
        },
      });
      this._inlineController.start(buildRecognitionContext(this.editText, this.inlineSelection.end, window.country));
    },
    cancelInlineDictation() {
      this._inlineGeneration = (this._inlineGeneration || 0) + 1;
      this._inlineTranscript?.freeze();
      this._inlineController?.dispose();
      this._inlineTranscript = null;
      this._inlineController = null;
      this.setInlineDictationState('idle');
    },
    onInlineVisibility() { if (document.hidden) this.cancelInlineDictation(); },
    onInlineCall(event) {
      this.dictationCallActive = event.detail?.active === true;
      if (this.dictationCallActive) this.cancelInlineDictation();
    },
    onOtherInlineEditor(event) { if (event.detail?.owner !== this._inlineOwner) this.cancelInlineDictation(); },
    onInlineLogout() {
      this.cancelInlineDictation();
      this.editing = false;
      this.editText = '';
      this.dictationRecovery = '';
      this.dictationEnabled = false;
    },
    async copyInlineRecovery() {
      try { await navigator.clipboard.writeText(this.dictationRecovery); this.dictationCopied = true; }
      catch { this.dictationCopied = false; }
    },
  },
};
